import { useState, useEffect } from 'react'
// import { AgentDesigner } from '../components/tools/AgentDesigner'
// import { PipelineEditor } from '../components/tools/PipelineEditor'
import { AIAssistant } from '../components/tools/AIAssistant'
import { Terminal } from '../components/tools/Terminal'
import { VaultBrowser } from '../components/tools/VaultBrowser'
import { synapseAPI } from '../lib/synapse-api'

type Tab = 'agents' | 'pipelines' | 'ai-chat' | 'terminal' | 'vault' | 'claude'

const TABS = [
  { id: 'agents' as Tab, label: 'Agents', icon: '🤖' },
  { id: 'pipelines' as Tab, label: 'Pipelines', icon: '⚙️' },
  { id: 'ai-chat' as Tab, label: 'AI Chat', icon: '💬' },
  { id: 'terminal' as Tab, label: 'Terminal', icon: '⌨️' },
  { id: 'vault' as Tab, label: 'Vault', icon: '🗄️' },
  { id: 'claude' as Tab, label: 'Claude', icon: '🤖' },
]

interface Task {
  id: string
  description: string
  status: string
  created_at: string
}

function ClaudePanel() {
  const [isHealthy, setIsHealthy] = useState(false)
  const [taskDescription, setTaskDescription] = useState('')
  const [recentTasks, setRecentTasks] = useState<Task[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')

  useEffect(() => {
    checkHealth()
    loadRecentTasks()
    const interval = setInterval(checkHealth, 10000)
    return () => clearInterval(interval)
  }, [])

  const checkHealth = async () => {
    try {
      await synapseAPI.getHealth()
      setIsHealthy(true)
    } catch {
      setIsHealthy(false)
    }
  }

  const loadRecentTasks = async () => {
    try {
      const tasks = await synapseAPI.getTasks(10)
      setRecentTasks(tasks || [])
    } catch (error) {
      console.error('Failed to load tasks:', error)
    }
  }

  const submitTask = async () => {
    if (!taskDescription.trim()) {
      setStatusMessage('Task description cannot be empty')
      return
    }

    setIsSubmitting(true)
    setStatusMessage('')

    try {
      const result = await synapseAPI.submitTask(taskDescription, 'NORMAL')
      setStatusMessage(`Task submitted: ${result.task_id}`)
      setTaskDescription('')
      loadRecentTasks()
    } catch (error) {
      setStatusMessage('Error submitting task')
    } finally {
      setIsSubmitting(false)
    }
  }

  const checkStatus = async (taskId: string) => {
    try {
      const data = await synapseAPI.getTask(taskId)
      setStatusMessage(`Task ${taskId}: ${data.status}`)
    } catch (error) {
      setStatusMessage('Failed to check status')
    }
  }

  const clearTasks = () => {
    setRecentTasks([])
    setStatusMessage('Tasks cleared')
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${isHealthy ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="font-medium">
            Synapse Connection: {isHealthy ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* Task Submission */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 space-y-4">
        <h3 className="text-lg font-semibold">Submit Task</h3>
        <textarea
          value={taskDescription}
          onChange={(e) => setTaskDescription(e.target.value)}
          placeholder="Enter task description..."
          className="w-full h-32 bg-[#0d1117] border border-[#30363d] rounded-lg p-3 text-white resize-none focus:outline-none focus:border-blue-500"
        />
        <div className="flex gap-3">
          <button
            onClick={submitTask}
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Task'}
          </button>
          <button
            onClick={clearTasks}
            className="px-4 py-2 bg-[#21262d] hover:bg-[#2d333b] rounded-lg font-medium transition-colors"
          >
            Clear
          </button>
        </div>
        {statusMessage && (
          <div className="text-sm text-gray-400 bg-[#0d1117] border border-[#30363d] rounded p-2">
            {statusMessage}
          </div>
        )}
      </div>

      {/* Recent Tasks */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Recent Tasks</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {recentTasks.length === 0 ? (
            <div className="text-gray-400 text-sm">No tasks yet</div>
          ) : (
            recentTasks.map((task) => (
              <div
                key={task.id}
                className="bg-[#0d1117] border border-[#30363d] rounded-lg p-3 hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => checkStatus(task.id)}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-blue-400">{task.id}</div>
                    <div className="text-sm text-gray-300 truncate">{task.description}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-1 rounded ${
                      task.status === 'completed' ? 'bg-green-600/20 text-green-400' :
                      task.status === 'failed' ? 'bg-red-600/20 text-red-400' :
                      task.status === 'running' ? 'bg-blue-600/20 text-blue-400' :
                      'bg-gray-600/20 text-gray-400'
                    }`}>
                      {task.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(task.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default function ToolsPage() {
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const saved = localStorage.getItem('tools-active-tab')
    return (saved as Tab) || 'agents'
  })

  useEffect(() => {
    localStorage.setItem('tools-active-tab', activeTab)
  }, [activeTab])

  const renderContent = () => {
    switch (activeTab) {
      case 'agents':
        return <div className="p-8 text-center text-gray-400">Agent Designer temporarily disabled</div>
      case 'pipelines':
        return <div className="p-8 text-center text-gray-400">Pipeline Editor temporarily disabled</div>
      case 'ai-chat':
        return <AIAssistant />
      case 'terminal':
        return <Terminal />
      case 'vault':
        return <VaultBrowser />
      case 'claude':
        return <ClaudePanel />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-[#0d1117] text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-[#161b22] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold mb-4">Tools</h1>

          {/* Tab Navigation */}
          <div className="flex gap-2 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2 rounded-lg font-medium whitespace-nowrap
                  transition-all duration-200 flex items-center gap-2
                  ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                      : 'bg-[#21262d] text-gray-400 hover:bg-[#2d333b] hover:text-white'
                  }
                `}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {renderContent()}
      </div>
    </div>
  )
}
